import { useTranslation } from "react-i18next";

import { GetDisplayLabelFunction } from "shared/schemas/types";

import { LandingPageEventType } from "pages/Dashboard/types";
import { LandingPageViewConfig } from "pages/shared/Dashboard/types";
import { getDefaultDetailsConfig } from "pages/shared/Dashboard/utils";
import { getAttributeValue } from "pages/shared/schemaDefinitions/utils";

import AiSparkleIcon from "features/ui/Icons/AiSparkleIcon";
import { MarkdownContent } from "features/ui/MarkdownContent";
import { SchemaEntry } from "features/ui/MaterialTable";

import RelatedEventsAndSmartGroups from "./RelatedEventsAndSmartGroups";

const AiSummaryAndFields = ({
  event,
  config,
  getDisplayLabel,
  getSchemaForAccessor,
}: {
  event: LandingPageEventType;
  config: LandingPageViewConfig;
  getDisplayLabel: GetDisplayLabelFunction;
  getSchemaForAccessor: (accessor: string) => SchemaEntry<string> | undefined;
}) => {
  const { t } = useTranslation();
  const detailsConfig =
    config.eventDetails ?? getDefaultDetailsConfig(config.eventType);

  return (
    <div className="flex-3 h-full flex flex-col gap-2 text-xs">
      {"AISummary" in event && (
        <div className="flex flex-col border border-purple-200 rounded-lg">
          <div className="bg-purple-100 rounded-t-lg p-2.5 font-bold border-b border-purple-200 flex gap-2 items-center">
            <div className="text-purple-400">
              <AiSparkleIcon size={14} />
            </div>
            <div>
              {getDisplayLabel("AISummary", t("schema:dashboard.ai_summary"))}
            </div>
          </div>
          <div className="bg-purple-50 rounded-b-lg flex-1 font">
            <div className="p-2.5 text-sm">
              {event.AISummary ? (
                <MarkdownContent content={event.AISummary} />
              ) : (
                t("common:not_available")
              )}
            </div>
            <RelatedEventsAndSmartGroups event={event} config={config} />
          </div>
        </div>
      )}
      {!config.eventDetailsNew?.useNewConfig && (
        <div className="grid grid-cols-2">
          {detailsConfig.mainFields.map((field, index) => {
            const totalItems = detailsConfig.mainFields.length;
            const isEven = totalItems % 2 === 0;
            const shouldRemoveBorder = isEven
              ? index >= totalItems - 2
              : index === totalItems - 1;

            const schemaForField = getSchemaForAccessor(field);

            const mainLabel =
              schemaForField?.description ?? schemaForField?.label ?? field;
            const subLabel = schemaForField?.label ?? field;

            return (
              <div
                key={field}
                className={`flex flex-col gap-2 py-3 border-b border-gray-50 ${
                  shouldRemoveBorder ? "border-b-0" : ""
                }`}
              >
                <div className="font-bold">
                  {mainLabel}
                  {mainLabel !== subLabel && ` (${subLabel})`}
                </div>
                <div className="pr-2">
                  {getAttributeValue(field, event, getSchemaForAccessor, t)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AiSummaryAndFields;
